import { useState, useEffect } from 'react';
import defaultHealthStoreLogo from '../assets/images/health_store_official_logo.jpg';

export const DEFAULT_HEALTH_STORE_LOGO = defaultHealthStoreLogo;
export const HEALTH_STORE_LOGO = defaultHealthStoreLogo;
export const HEALTH_STORE_FALLBACK_LOGO = '/assets/images/health_store_official_logo.jpg';

export const STORE_LOGO_STORAGE_KEY = 'healthstore_custom_store_logo_url';

/**
 * Validates whether a given URL is a valid, permanent, non-blob image URL
 */
export function isValidLogoUrl(url: unknown): url is string {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (trimmed.length < 5) return false;
  if (trimmed === 'null' || trimmed === 'undefined' || trimmed === '[object Object]') return false;
  if (trimmed.startsWith('blob:')) return false;
  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:image/')
  );
}

/**
 * Returns the currently active store logo URL (custom uploaded from Firestore/localStorage or bundled default).
 * Strictly validates that the URL is working, permanent, and not a dead temporary blob URL.
 */
export function getStoreLogo(): string {
  if (typeof window === 'undefined') return DEFAULT_HEALTH_STORE_LOGO;
  try {
    const custom = localStorage.getItem(STORE_LOGO_STORAGE_KEY);
    if (isValidLogoUrl(custom)) {
      return custom.trim();
    }
  } catch {
    // ignore
  }
  return DEFAULT_HEALTH_STORE_LOGO;
}

/**
 * Saves and broadcasts a new custom store logo URL across all open components.
 */
export function setStoreLogo(url: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    if (isValidLogoUrl(url)) {
      localStorage.setItem(STORE_LOGO_STORAGE_KEY, url.trim());
    } else {
      localStorage.removeItem(STORE_LOGO_STORAGE_KEY);
    }
    // Broadcast event for same-tab updates
    window.dispatchEvent(new CustomEvent('healthstore:logo_updated', { detail: isValidLogoUrl(url) ? url.trim() : null }));
  } catch (err) {
    console.warn('Could not persist logo to localStorage:', err);
  }
}

/**
 * React hook that returns the active store logo URL and stays reactive to changes.
 */
export function useStoreLogo(): string {
  const [logoUrl, setLogoUrl] = useState<string>(() => getStoreLogo());

  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<string | null>;
      if (isValidLogoUrl(customEvent.detail)) {
        setLogoUrl(customEvent.detail);
      } else {
        setLogoUrl(getStoreLogo());
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORE_LOGO_STORAGE_KEY) {
        const val = e.newValue;
        if (isValidLogoUrl(val)) {
          setLogoUrl(val);
        } else {
          setLogoUrl(DEFAULT_HEALTH_STORE_LOGO);
        }
      }
    };

    window.addEventListener('healthstore:logo_updated', handleUpdate);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('healthstore:logo_updated', handleUpdate);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  return isValidLogoUrl(logoUrl) ? logoUrl : DEFAULT_HEALTH_STORE_LOGO;
}

